import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Container,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Background from "../../components/Background";
import Sidebar from "../../components/Sidebar";
import Mobilesidebar from "../../components/Mobilesidebar";
import useValidation from "../../hooks/useValidation";
import { changePassword, clearChangePasswordData } from "../../redux/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { toggleSessionModal } from "../../redux/sessionSlice";
import MenteeHeader from "../../components/MenteeHeader";
import tickmark from "../../assets/images/Tick Mark.svg";
import tickmarkSolid from "../../assets/images/check-solid.png";
import { styled } from "@mui/system";
import SuccessModal from "../../components/SuccessModal";
import DashboardFooter from "../../components/Footer";

const RoundedTextField = styled(TextField)({
  borderRadius: "20px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "20px",
    backgroundColor: "#F1F7FA", // Set the input background color
  },
  "& .MuiInputBase-input": {
    padding: "10px",
    color: "#2A4876", // Set the input value color to green
    "&::placeholder": {
      color: "#151516", // Set the placeholder text color to red
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent", // Optional: make the border transparent
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent", // Optional: ensure border stays transparent on hover
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent", // Optional: ensure border stays transparent when focused
  },
});

const StyledIconButton = styled(IconButton)({
  width: "40px",
  height: "40px",
  color: "#7BB9D5",

  borderRadius: "50%", // Make it circular
  "&:hover": {
    backgroundColor: "#E0E7E9", // Slightly darker on hover
  },
});

const validationArr = [
  {
    name: "specialChar",
    text: `1 special character (Any)`,
  },
  {
    name: "uppercase",
    text: `1 uppercase letter (A - Z)`,
  },
  {
    name: "number",
    text: `1 number (0 - 9)`,
  },
  {
    name: "minLength",
    text: `Minimum 8 characters long`,
  },
];

const ChangePassword = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRetypePassword, setShowRetypePassword] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [formValues, setFormValues] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const { errors, validateInputs, resetValidation } = useValidation({
    currentPassword: formValues.currentPassword,
    newPassword: formValues.newPassword,
    confirmPassword: formValues.confirmPassword,
  });

  const [validations, setValidations] = useState({
    specialChar: false,
    uppercase: false,
    number: false,
    minLength: false,
  });

  const validatePassword = (password) => {
    setValidations({
      specialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password), // Check special char
      uppercase: /[A-Z]/.test(password), // Check for uppercase letter
      number: /\d/.test(password), // Check for number
      minLength: password.length >= 8, // Check minimum length
    });
  };

  useEffect(() => {
    if (auth.changePwdStatus === "failed") {
      dispatch(clearChangePasswordData());
    }
  }, []);
  const handleTogglePasswordVisibility = (type) => {
    if (type === "current") setShowPassword(!showPassword);
    if (type === "new") setShowNewPassword(!showNewPassword);
    if (type === "retype") setShowRetypePassword(!showRetypePassword);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
    name === "newPassword" && validatePassword(value);
    if (errors) {
      resetValidation();
    }
    if (auth.changePwdStatus === "failed") {
      dispatch(clearChangePasswordData());
    }
  };
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  const handleChangePassword = () => {
    if (!validateInputs()) {
      return;
    }
    const payload = {
      currentPassword: formValues.currentPassword,
      newPassword: formValues.newPassword,
    };
    dispatch(changePassword(payload))
      .then((response) => {
        console.log("response", response);
        if (response?.error?.message === "Please authenticate") {
          dispatch(toggleSessionModal(false));
        }
        if (response?.payload?.data?.changePassword?.success) {
          setOpen(true);
          setFormValues({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
          setValidations({
            specialChar: false,
            uppercase: false,
            number: false,
            minLength: false,
          });
        }
      })
      .catch((error) => {
        console.log("err", error);
        if (error === "Please authenticate") {
          dispatch(toggleSessionModal(false));
        }
      });
  };

  const hasAtLeastOneFalse = Object.values(validations).some((val) => !val);
  const isDisabled =
    formValues.currentPassword &&
    formValues.newPassword &&
    formValues.confirmPassword &&
    Object.keys(errors).length === 0 &&
    !hasAtLeastOneFalse;

  const passwordRules = validationArr?.map((item) => (
    <Typography
      key={item?.name}
      sx={{ marginBottom: "3px" }}
      variant="body2"
      className="text-xs text-text-color"
    >
      <img
        alt=""
        src={validations[item?.name] ? tickmarkSolid : tickmark}
        width={"14px"}
        height={"14px"}
        style={{ marginRight: "5px" }}
      />{" "}
      {item?.text}
    </Typography>
  ));

  return (
    <>
      <Background>
        <MenteeHeader
          searchTerm={searchTerm}
          handleSearchChange={handleSearchChange}
        />
        <Box
          sx={{
            padding: {
              xs: "10px 0 20px",
              sm: "10px 0 20px",
              md: "10px 0 20px",
              lg: "10px 0 20px",
              xl: "20px 0 30px",
            },
          }}
        >
          <Container maxWidth="xl">
            <Grid container spacing={{ lg: 3, sm: 2, xs: 2 }}>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <Mobilesidebar />
                <Sidebar />
              </Grid>
              {/* Main Content Section */}
              <Grid item xs={12} sm={8} md={9} lg={9}>
                <Box
                  sx={{
                    overflow: "none", // Enable scrolling if content exceeds the height
                    padding: "12px 24px",
                    borderRadius: "20px",
                    backgroundColor: "#fff",
                    boxShadow: "0px 20px 20px 0px #2A487610",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: "left",
                      fontFamily: "PT Sans",
                      marginBottom: "8px",
                      fontWeight: "bold",
                      color: "#2A4876",
                      fontSize: {
                        xs: "16px",
                        sm: "18px",
                        md: "20px",
                        lg: "1.25rem",
                      },
                    }}
                  >
                    Change Password
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: {
                        xs: "calc(100vh - 290px)",
                        sm: "calc(100vh - 262px)",
                      }, // Limit the height of the Box
                      overflowY: "auto",
                    }}
                  >
                    {/* lg+: fields left, password rules fill the otherwise
                        empty right half of the panel */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: { lg: 6 },
                        alignItems: "flex-start",
                      }}
                    >
                      <Box
                        sx={{
                          width: {
                            xs: "100%",
                            sm: "75%",
                            md: "75%",
                            lg: "55%",
                            xl: "55%",
                          },
                        }}
                      >
                        {/* Current Password */}
                        <Typography
                          sx={{
                            marginBottom: "5px",
                            color: "#152134",
                            fontSize: "14px",
                            textAlign: "left",
                          }}
                        >
                          Current Password
                        </Typography>
                        <RoundedTextField
                          name="currentPassword"
                          variant="outlined"
                          fullWidth
                          placeholder="Enter here"
                          onChange={(e) => handleChange(e)}
                          value={formValues.currentPassword}
                          error={errors.currentPassword}
                          helperText={
                            <p className="error-msg">
                              {errors.currentPassword}
                            </p>
                          }
                          type={showPassword ? "text" : "password"}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <StyledIconButton
                                  onClick={() =>
                                    handleTogglePasswordVisibility("current")
                                  }
                                  edge="end"
                                >
                                  {showPassword ? (
                                    <Visibility />
                                  ) : (
                                    <VisibilityOff />
                                  )}
                                </StyledIconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "50px",
                              backgroundColor: "#F8FAFC",
                              border: "1px solid #D0D4DA",
                            },
                          }}
                        />

                        {/* New Password */}
                        <Typography
                          sx={{
                            marginBottom: "5px",
                            color: "#152134",
                            fontSize: "14px",
                            marginTop: "10px",
                            textAlign: "left",
                          }}
                        >
                          New Password
                        </Typography>
                        <RoundedTextField
                          name="newPassword"
                          fullWidth
                          placeholder="Enter here"
                          onChange={(e) => handleChange(e)}
                          value={formValues.newPassword}
                          error={errors.newPassword}
                          helperText={
                            <p className="error-msg">{errors.newPassword}</p>
                          }
                          type={showNewPassword ? "text" : "password"}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <StyledIconButton
                                  onClick={() =>
                                    handleTogglePasswordVisibility("new")
                                  }
                                  edge="end"
                                >
                                  {showNewPassword ? (
                                    <Visibility />
                                  ) : (
                                    <VisibilityOff />
                                  )}
                                </StyledIconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            marginBottom: "8px",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "50px",
                              backgroundColor: "#F8FAFC",
                              border: "1px solid #D0D4DA",
                            },
                          }}
                        />

                        {/* Validation List */}
                        <Box
                          className="password-rules"
                          sx={{ display: { xs: "block", lg: "none" } }}
                        >
                          {passwordRules}
                        </Box>

                        {/* Retype New Password */}
                        <Typography
                          sx={{
                            marginBottom: "5px",
                            color: "#152134",
                            fontSize: "14px",
                            marginTop: "10px",
                            textAlign: "left",
                          }}
                        >
                          Retype New Password
                        </Typography>
                        <RoundedTextField
                          name="confirmPassword"
                          fullWidth
                          placeholder="Enter here"
                          onChange={(e) => handleChange(e)}
                          value={formValues.confirmPassword}
                          error={errors.confirmPassword}
                          helperText={
                            <p className="error-msg">
                              {errors.confirmPassword}
                            </p>
                          }
                          type={showRetypePassword ? "text" : "password"}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <StyledIconButton
                                  onClick={() =>
                                    handleTogglePasswordVisibility("retype")
                                  }
                                  edge="end"
                                >
                                  {showRetypePassword ? (
                                    <Visibility />
                                  ) : (
                                    <VisibilityOff />
                                  )}
                                </StyledIconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "50px",
                              backgroundColor: "#F8FAFC",
                              border: "1px solid #D0D4DA",
                            },
                          }}
                        />
                        <Box
                          mt={2}
                          sx={{ display: "flex", justifyContent: "start" }}
                        >
                          {auth.changePwdStatus === "failed" && (
                            <Typography
                              sx={{ color: "red", marginBottom: "15px" }}
                            >
                              {auth.changePwdError}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: { xs: "none", lg: "block" },
                          flex: 1,
                          backgroundColor: "#F8FAFC",
                          border: "1px solid #E3EAF1",
                          borderRadius: "20px",
                          padding: "32px 36px",
                          marginTop: "24px",
                          textAlign: "left",
                          // Scoped to the card so the inline (sub-lg) copy
                          // of the list keeps its compact sizing.
                          "& .text-xs": {
                            fontSize: "13.5px",
                            marginBottom: "12px",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            color: "#2A4876",
                            fontSize: "15px",
                            marginBottom: "16px",
                            textAlign: "left",
                          }}
                        >
                          Your new password must contain:
                        </Typography>
                        {passwordRules}
                      </Box>
                    </Box>
                  </Box>

                  <Box mt={1} sx={{ display: "flex", justifyContent: "start" }}>
                    <Button
                      className="text-white"
                      variant="contained"
                      sx={{
                        backgroundColor: "#2A4876",
                        "&:disabled": {
                          backgroundColor: "#AAB6C8",
                        },
                        fontWeight: 700,
                        fontSize: "14px",
                        lineHeight: "18px",
                        width: "50%",
                        height: {
                          xs: "40px",
                          sm: "45px",
                          md: "45px",
                          lg: "50px",
                          xl: "60px",
                        },
                        borderRadius: "100px",
                        textTransform: "none",
                      }}
                      disabled={
                        !isDisabled || auth.changePwdStatus === "loading"
                      }
                      onClick={handleChangePassword}
                    >
                      Save Changes
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
        {console.log("open", open)}
        {open && (
          <SuccessModal
            open={open}
            onClose={() => {
              setOpen(false);
            }}
            title="Your password has been changed successfully"
          />
        )}
      </Background>
      <DashboardFooter />
    </>
  );
};

export default ChangePassword;
